import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import './Views.scss';
import { SUBTABS, CHOOSE_STEPS, SETUP_STEPS } from './ConstPaths';
import { extractBodySiteButtons } from './ConstBodySiteBtns';
import { CONTROL_ACCESSORIES } from './ConstAccessMethodsMock';
import { SELECTION_METHOD_BUTTONS } from './ConstSelectionMethodBtns';
import { LABELS_FOR_SETUP } from './ConstForTestDriveSection';
import ReviewRow from '../Components/layout/ReviewRow';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_TRY_UPDATE_HELP = `
  <div>
    <p>Generally, you want to keep the access method setup the same throughout a Test-drive. 
    (Just like you would only test-drive one car at a time.)  
    But there may be times where you might have made changes in the middle of an activity, so that by the end of the Test-drive, the setup is a bit different than when you started.
    </p>
    <p>
    So here you can enter those changes, if there were any.  
    This represents the access method setup that you will be rating in the next steps.  
    You can also edit the setup name here.   
    </p>
    <p>
    If you didn't change anything about the setup, just Continue on to the next step.
    </p>
  </div>
`;

const propTypes = {
  // firstName: PropTypes.string,
  // pronouns: PropTypes.arrayOf(PropTypes.string),
  activeAccessMethodName: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupBodySiteComments: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupPositioningNotes: PropTypes.string,
  activeSetupOtherNotes: PropTypes.string,
  activeSetupName: PropTypes.string,
  onChangeActiveSetupBodySiteComments: PropTypes.func.isRequired,
  onChangeActiveSetupPositioningNotes: PropTypes.func.isRequired,
  onChangeActiveSetupOtherNotes: PropTypes.func.isRequired,
  onChangeActiveSetupName: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired
};

const defaultProps = {
  // firstName: 'the client',
  // pronouns: ['they', 'them', 'their'],
  activeAccessMethodName: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupBodySiteComments: '',
  activeSetupAccessories: [],
  activeSetupPositioningNotes: '',
  activeSetupOtherNotes: '',
  activeSetupName: ''
};

//content sub-component with unique content for this page
function TestDriveTryUpdateContent(props) {
  // const firstName = props.firstName;
  // const pronouns = props.pronouns;
  // const update_path = SUBTABS.SETUP + "/" + SETUP_STEPS.INFO;
  const activeAccessMethodName = props.activeAccessMethodName;
  const activeSetupSelectionMethod = props.activeSetupSelectionMethod
  const activeSetupBodySite = props.activeSetupBodySite;
  const activeSetupBodySiteComments = props.activeSetupBodySiteComments;
  const activeSetupAccessories = props.activeSetupAccessories;
  const activeSetupPositioningNotes = props.activeSetupPositioningNotes;
  const activeSetupOtherNotes = props.activeSetupOtherNotes;
  const activeSetupName = props.activeSetupName;
  const onChangeActiveSetupBodySiteComments = props.onChangeActiveSetupBodySiteComments;
  const onChangeActiveSetupPositioningNotes = props.onChangeActiveSetupPositioningNotes;
  const onChangeActiveSetupOtherNotes = props.onChangeActiveSetupOtherNotes;
  const onChangeActiveSetupName = props.onChangeActiveSetupName;

  const bodySiteBtns = extractBodySiteButtons();

  return(
  <>
  <Row>
    <Col>
      <p>If so, take a moment to update the Test-drive setup so it accurately reflects the setup that you will be rating. This lets us (and you!) track your results with less confusion.</p>
      <p>Just hit Update, make the change, then hit Back to return here.</p>
    </Col>
  </Row>
  <Row className="aa-review-row">
    <div className="col-md-1">
      <span className={`fa fa-gears fa-2x aa-testdrive-color`}></span>
    </div>
    <div className="col">
      {/* <h5>Setup Info</h5> */}
      <div className="col">
        <ReviewRow 
          border="none"
          padded="none"
          heading={LABELS_FOR_SETUP.ACCESS_METHOD}
          nextRoute={SUBTABS.CHOOSE+"/"+CHOOSE_STEPS.ACCESS_METHOD}
          values={activeAccessMethodName}
          lookup={null}
        />
        <ReviewRow 
          border="none"
          padded="none"
          heading={LABELS_FOR_SETUP.SELECTION_METHOD}
          nextRoute={SUBTABS.SETUP+"/"+SETUP_STEPS.SELECTION_METHOD}
          values={activeSetupSelectionMethod}
          lookup={SELECTION_METHOD_BUTTONS}
        />
        <ReviewRow 
          border="none"
          padded="none"
          heading={LABELS_FOR_SETUP.BODY_SITES}
          nextRoute={SUBTABS.SETUP+"/"+SETUP_STEPS.BODY_SITES}
          values={activeSetupBodySite}
          lookup={bodySiteBtns}
        />
        <Form>
          <FormGroup>
            <Label for="body-site-comments" className="aa-bold-text">
              {LABELS_FOR_SETUP.BODY_SITE_COMMENTS+":"}
            </Label>
            <Input
              id="body-site-comments"
              value={activeSetupBodySiteComments}
              onChange={onChangeActiveSetupBodySiteComments}
              name="body-site-comments"
              type="textfield"
            />
          </FormGroup>
        </Form>
        <ReviewRow 
          border="none"
          padded="none"
          heading={LABELS_FOR_SETUP.ACCESSORIES}
          nextRoute={SUBTABS.SETUP+"/"+SETUP_STEPS.ACCESSORIES}
          values={activeSetupAccessories}
          lookup={CONTROL_ACCESSORIES}
        />
        <Form>
          <FormGroup>
            <Label for="positioning-notes" className="aa-bold-text">
              Positioning notes:
            </Label>
            <Input
              id="positioning-notes"
              value={activeSetupPositioningNotes}
              onChange={onChangeActiveSetupPositioningNotes}
              name="positioning-notes"
              type="textarea"
            />
          </FormGroup>
          <FormGroup>
            <Label for="other-notes" className="aa-bold-text">
              {LABELS_FOR_SETUP.OTHER+":"}
            </Label>
            <Input
              id="other-notes"
              value={activeSetupOtherNotes}
              onChange={onChangeActiveSetupOtherNotes}
              name="other-notes"
              type="textarea"
            />
          </FormGroup>
          <FormGroup>
            <Label for="setup-name" className="aa-bold-text">
              {LABELS_FOR_SETUP.NAME+":"}
            </Label>
            <Input
              id="setup-name"
              value={activeSetupName}
              onChange={onChangeActiveSetupName}
              name="setup-name"
              type="textfield"
            />
          </FormGroup>
        </Form>
      </div>
    </div>
  </Row>
  <Row>
    <Col>
      If you didn't change anything in the setup, just click Continue to move on.    
    </Col>
  </Row>
  </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveTryUpdate(props) {
  // const firstName = props.firstName;
  // const pronouns = props.pronouns;
  const activeAccessMethodName = props.activeAccessMethodName;
  const activeSetupSelectionMethod = props.activeSetupSelectionMethod;
  const activeSetupBodySite = props.activeSetupBodySite;
  const activeSetupBodySiteComments = props.activeSetupBodySiteComments;
  const activeSetupAccessories = props.activeSetupAccessories;
  const activeSetupPositioningNotes = props.activeSetupPositioningNotes;
  const activeSetupOtherNotes = props.activeSetupOtherNotes;
  const activeSetupName = props.activeSetupName;
  const onChangeActiveSetupBodySiteComments = props.onChangeActiveSetupBodySiteComments;
  const onChangeActiveSetupPositioningNotes = props.onChangeActiveSetupPositioningNotes;
  const onChangeActiveSetupOtherNotes = props.onChangeActiveSetupOtherNotes;
  const onChangeActiveSetupName = props.onChangeActiveSetupName;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-car aa-fa-3x",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Before rating, did you change anything in the setup during that Test-drive?`,
    subTitle: `For example, maybe you adjusted the positioning or ended up with a different body site by the end of the activity.`,
    learnMore: {linkText: "Learn More about updating a Test-drive setup",
                title: "Updating a Test-drive",
                content: TD_TRY_UPDATE_HELP
                },
    nextRoute: "", //not needed since we are using on
    sidebar: false
  }

  console.log("Rendering TestDriveTryUpdate, setupName: " + activeSetupName);
  
  useEffect(() => {
    console.log('TestDriveTryUpdate mounted');
    return () => {
      console.log('TestDriveTryUpdate unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveTryUpdateContent 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupBodySiteComments={activeSetupBodySiteComments}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupPositioningNotes={activeSetupPositioningNotes}
          activeSetupOtherNotes={activeSetupOtherNotes}
          activeSetupName={activeSetupName}
          onChangeActiveSetupBodySiteComments={onChangeActiveSetupBodySiteComments}
          onChangeActiveSetupPositioningNotes={onChangeActiveSetupPositioningNotes}
          onChangeActiveSetupOtherNotes={onChangeActiveSetupOtherNotes}
          onChangeActiveSetupName={onChangeActiveSetupName}
        />
      } 
      sidebarComponent = {
        dev.showDevSidebar && 
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
       />
      }
      onContinue={onContinue}  
    />
    </>
  );
}

TestDriveTryUpdate.propTypes = propTypes;
TestDriveTryUpdate.defaultProps = defaultProps;

export default TestDriveTryUpdate;